import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, signal, inject } from '@angular/core';
import { AccountingApiService } from '../../../../_services/accounting-service/accounting-api/accounting-api.service';
import { AccountingContact } from '../../../../_interfaces/accounting.interface';
import { NgSelectComponent } from '@ng-select/ng-select';
import { BoFormsModule } from '@bo/ng-forms';
import { concat, distinctUntilChanged, merge, Observable, of, Subject, Subscription, throwError, timer } from 'rxjs';
import { AsyncPipe, NgClass } from '@angular/common';
import { SelectOption } from '../../../../_interfaces/select-field.interface';
import { catchError, debounceTime, retry, switchMap, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-contacts-select',
  imports: [NgSelectComponent, BoFormsModule, NgClass, AsyncPipe],
  templateUrl: './contacts-select.component.html',
})
export class ContactsSelectComponent implements OnInit, OnDestroy {
  readonly accountingApiService = inject(AccountingApiService);

  @Input() filterLabel: boolean = false;
  @Input() set selectedContact(value: string | undefined | null) {
    // ng-select requires null (not undefined or empty string) to show placeholder
    this._selectedContact = value || null;
  }
  get selectedContact(): string | undefined | null {
    return this._selectedContact;
  }
  protected _selectedContact: string | null = null;
  @Output() selectionChanged: EventEmitter<string | undefined> = new EventEmitter<string | undefined>();

  contacts$!: Observable<SelectOption[]>;
  searchInput$ = new Subject<string>();
  private readonly contactsSubject$ = new Subject<SelectOption[]>();
  loadingContacts = signal(false);

  // Pagination state
  private currentPage = 1;
  private readonly PAGE_SIZE = 50;
  private hasMorePages = true;
  private allLoadedContacts: SelectOption[] = [];
  private isSearchMode = false;
  private currentSearchTerm = '';

  readonly subscriptions = new Subscription();

  ngOnInit(): void {
    this.initializeContactsObservable();
  }

  /**
   * Initialize the contacts observable with typeahead search and initial load
   */
  private initializeContactsObservable(): void {
    this.contacts$ = merge(
      concat(
        this.loadInitialContactsObservable(),
        this.searchInput$.pipe(
          debounceTime(300),
          distinctUntilChanged(),
          tap(() => this.loadingContacts.set(true)),
          switchMap((term) => this.searchContacts(term)),
        ),
      ),
      this.contactsSubject$.asObservable(),
    );
  }

  /**
   * Load the initial page of contacts and return as observable
   */
  private loadInitialContactsObservable(): Observable<SelectOption[]> {
    this.loadingContacts.set(true);
    this.isSearchMode = false;
    this.currentPage = 1;
    this.allLoadedContacts = [];

    return this.accountingApiService.getAccountingContacts(this.currentPage, this.PAGE_SIZE).pipe(
      retry({
        count: 2,
        delay: (error: HttpErrorResponse) => {
          if (error.status === 401 || error.status === 403) {
            return timer(1000);
          }
          return throwError(() => error);
        },
      }),
      tap((data) => {
        this.allLoadedContacts = this.mapContactsToSelectOptions(data.results);
        this.hasMorePages = !!data.next;
        this.loadingContacts.set(false);
      }),
      switchMap(() => of(this.allLoadedContacts)),
      catchError(() => {
        this.loadingContacts.set(false);
        return of([]);
      }),
    );
  }

  /**
   * Search contacts by name (server-side search)
   */
  private searchContacts(searchTerm: string): Observable<SelectOption[]> {
    this.currentSearchTerm = searchTerm;

    if (!searchTerm || searchTerm.trim() === '') {
      this.isSearchMode = false;
      this.currentSearchTerm = '';
      return this.loadInitialContactsObservable();
    }

    this.isSearchMode = true;
    this.currentPage = 1;

    return this.accountingApiService.getAccountingContacts(this.currentPage, this.PAGE_SIZE, searchTerm).pipe(
      retry({
        count: 2,
        delay: (error: HttpErrorResponse) => {
          if (error.status === 401 || error.status === 403) {
            return timer(1000);
          }
          return throwError(() => error);
        },
      }),
      tap((data) => {
        this.allLoadedContacts = this.mapContactsToSelectOptions(data.results);
        this.hasMorePages = !!data.next;
        this.loadingContacts.set(false);
      }),
      switchMap(() => of(this.allLoadedContacts)),
      catchError(() => {
        this.loadingContacts.set(false);
        return of([]);
      }),
    );
  }

  /**
   * Load more contacts when scrolling to the end (infinite scroll)
   */
  onScrollToEnd(): void {
    if (!this.hasMorePages || this.loadingContacts()) {
      return;
    }

    this.loadingContacts.set(true);
    this.currentPage++;

    const searchTerm = this.isSearchMode ? this.currentSearchTerm : undefined;

    this.accountingApiService
      .getAccountingContacts(this.currentPage, this.PAGE_SIZE, searchTerm)
      .pipe(
        retry({
          count: 2,
          delay: (error: HttpErrorResponse) => {
            if (error.status === 401 || error.status === 403) {
              return timer(1000);
            }
            return throwError(() => error);
          },
        }),
        catchError(() => {
          this.currentPage--;
          this.loadingContacts.set(false);
          return throwError(() => new Error('Failed to load more contacts'));
        }),
      )
      .subscribe({
        next: (data) => {
          const newContacts = this.mapContactsToSelectOptions(data.results);
          this.allLoadedContacts = [...this.allLoadedContacts, ...newContacts];
          this.hasMorePages = !!data.next;
          this.loadingContacts.set(false);

          this.contactsSubject$.next([...this.allLoadedContacts]);
        },
      });
  }

  /**
   * Preload next page when scrolling reaches 80%
   */
  onScroll(event: { start: number; end: number }): void {
    if (!this.hasMorePages || this.loadingContacts()) {
      return;
    }

    const threshold = 0.8;
    const currentIndex = event.end;
    const totalItems = this.allLoadedContacts.length;

    if (currentIndex >= totalItems * threshold) {
      this.onScrollToEnd();
    }
  }

  /**
   * Map API contacts to SelectOption format, filtering empty names
   */
  private mapContactsToSelectOptions(contacts: AccountingContact[]): SelectOption[] {
    return contacts
      .filter((contact) => contact.name && contact.name.trim() !== '')
      .map<SelectOption>((contact) => ({
        option: contact.name!,
        value: contact.name!,
      }));
  }

  changeSelection(): void {
    this.selectionChanged.emit(this._selectedContact ?? undefined);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
